import {
  addDays as addD,
  addMonths as addM,
  addMinutes as addMin,
  addSeconds as addSec,
  differenceInHours,
  differenceInMinutes,
  isAfter,
  isEqual,
  isSameDay,
  isValid,
  parseISO,
} from "date-fns";
import { formatInTimeZone as formatInTZ } from "date-fns-tz";
import { toString } from "duration-fns";

import { config } from "shared/data";

import { isNumeric, isString } from "./types";

export function parseDate(value: Date | number | string) {
  if (isString(value)) {
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : new Date(value);
  } else if (isNumeric(value)) {
    return new Date(value);
  }
  return value;
}

export function durationToISOString(minutes: number, seconds: number) {
  return toString({
    minutes,
    seconds,
  });
}

export function formatInTimeZone(
  value: Date | number | string,
  format: string = config.date.defaultFormat,
  timeZone: string = config.date.defaultTimeZone,
) {
  return formatInTZ(parseDate(value), timeZone, format);
}

export function formatDistanceToNow(value: Date | number | string) {
  const now = Date.now();
  const parsed = parseDate(value);
  const minutes = differenceInMinutes(now, parsed, { roundingMethod: "round" });
  const hours = differenceInHours(now, parsed, { roundingMethod: "round" });
  switch (true) {
    case minutes <= 1:
      return "1 minute ago";
    case minutes > 1 && minutes < 60:
      return `${minutes} minutes ago`;
    case hours === 1:
      return `1 hour ago`;
    case hours > 1 && hours <= 6:
      return `${hours} hours ago`;
    default:
      return formatInTimeZone(parsed);
  }
}

export function toISOString(value: Date | number | string) {
  return parseDate(value).toISOString();
}

export function addDays(value: Date | number | string, amount: number) {
  return addD(parseDate(value), amount);
}

export function addMonths(value: Date | number | string, amount: number) {
  return addM(parseDate(value), amount);
}

export function addMinutes(value: Date | number | string, amount: number) {
  return addMin(parseDate(value), amount);
}

export function addSeconds(value: Date | number | string, amount: number) {
  return addSec(parseDate(value), amount);
}

export function isToday(value: Date | number | string) {
  return isSameDay(parseDate(value), new Date());
}

export function isYesterday(value: Date | number | string) {
  return isSameDay(parseDate(value), addDays(new Date(), -1));
}

export function isAfterOrEqual(
  value: Date | number | string,
  compareValue: Date | number | string,
) {
  const dateA = parseDate(value);
  const dateB = parseDate(compareValue);
  return isAfter(dateA, dateB) || isEqual(dateA, dateB);
}
